import moment from 'moment';
import {Types} from 'mongoose';
import store from '@/store';
import {SearchStatus} from '@/classes/dto/finder/request/Search.request.dto';
import {EventManagerApi} from '@/classes/api/event-manager.api.class';
import {CrudEntityTypes} from '@/classes/clientOnly/permissionTreeResources/enums/CrudEntityTypes';
import {NavigationDrawerAccess} from '@/enums/_common/NavigationDrawerAccess.enum';
import {EventInfo, EventParticipantListItem, EventParticipantType} from '@/interfaces/eventInfo.interface';
import {isObjectId} from '@/helpers/isObjectId.helper';
import {
    capacityOf,
    formatDuration,
    freeSpots,
    idString,
    longDate,
    occupiedSpots,
    organizerNames,
    participantsOf,
    STATUS_ITEMS,
    statusKey,
    timeRange,
    topicName,
    venueSummary,
} from '@/components/trainings/trainings.helper';
import {seriesLabel} from '@/components/trainings/series/trainingSeries.helper';
import {canReadEntity, canReadType} from '@/components/app/search/searchAccess';
import {PaletteHit} from '@/components/app/search/searchEntities';
import {QuickContext} from '@/components/app/search/searchQuickViewData';

/** Minimum term length of the server search (same as the "Alle Zeiten" search of /trainings) */
export const MIN_TRAINING_TERM = 2;
const MAX_DESCRIPTION = 180;
const MAX_NAMES = 4;

export type TrainingResult = { total: number; items: PaletteHit[] };

/**
 * May the user search trainings: the menu item "Schulungen" is granted (same gate as the navigation)
 * and the user may read start and topic of the trainings (CRUD check before every request).
 */
export function canSearchTrainings(): boolean {
    const granted = NavigationDrawerAccess.getEffectiveAccess((store.state.guiAccess || []) as NavigationDrawerAccess[]);
    return granted.includes(NavigationDrawerAccess.TRAININGS) && canReadType(CrudEntityTypes.EVENT_INFO, 'startTime', 'topic');
}

/**
 * "Aktiv" = not cancelled and starting today or later (same scope as "Kommende" on /trainings, so a training of today
 * stays active the whole day). "Inaktiv" = cancelled or of an earlier day.
 */
export function isTrainingActive(training: Pick<EventInfo, 'startTime' | 'isCancelled'>): boolean {
    return !training.isCancelled && !moment(training.startTime).isBefore(moment().startOf('day'));
}

/** "Mo, 12.10.2026 · 14:00–16:00 Uhr" */
function whenText(training: EventInfo): string {
    if (!training.startTime) {
        return '';
    }
    return `${moment(training.startTime).format('dd, DD.MM.YYYY')} · ${timeRange(training)}`;
}

function titleOf(training: EventInfo): string {
    const topic = topicName(training);
    return training.title ? `${training.title} · ${topic}` : topic;
}

/** Maps a training to the hit shape of the finder (results, highlighting, keyboard, visits and pins work unchanged) */
export function trainingHit(training: EventInfo): PaletteHit {
    const active = isTrainingActive(training);
    const when = whenText(training);
    const venue = training.venue ? venueSummary(training).text : '';
    let status: string | undefined;
    if (training.isCancelled) {
        status = 'abgesagt';
    } else if (!active) {
        status = 'vergangen';
    }
    return {
        id: idString(training._id),
        entity: 'trainings',
        title: titleOf(training),
        subtitle: [when, venue].filter((el) => el).join(' · '),
        isActive: active,
        status,
        score: 0,
        preview: {when, venue, topic: topicName(training), title: training.title || ''},
    };
}

function topicPopulate(): Record<string, { fields: string[] }> {
    return canReadType(CrudEntityTypes.EVENT_TOPIC, 'name') ? {topic: {fields: ['name']}} : {};
}

/** Upcoming first (soonest first), then past / cancelled ones (latest first) */
function sortTrainings(trainings: EventInfo[]): EventInfo[] {
    const time = (training: EventInfo) => moment(training.startTime).valueOf();
    const active = trainings.filter(isTrainingActive).sort((a, b) => time(a) - time(b));
    const inactive = trainings.filter((training) => !isTrainingActive(training)).sort((a, b) => time(b) - time(a));
    return [...active, ...inactive];
}

/**
 * Searches trainings with the server search of /trainings ("Alle Zeiten": event-info with filter.$search).
 * The status is applied in the frontend (see isTrainingActive). Returns null if the user may not search trainings.
 */
export async function runTrainingSearch(term: string, status: SearchStatus, limit: number): Promise<TrainingResult | null> {
    const value = term.trim();
    if (!canSearchTrainings() || value.length < MIN_TRAINING_TERM) {
        return null;
    }
    const resp = await EventManagerApi.findEventInfos({
        filter: {$search: value},
        skipPagination: true,
        populate: topicPopulate(),
        sortBy: 'startTime',
        sortDesc: true,
    } as any);
    const matching = (resp?.eventInfos || []).filter((training) => {
        if (status === 'all') {
            return true;
        }
        return isTrainingActive(training) === (status === 'active');
    });
    const sorted = sortTrainings(matching);
    return {total: sorted.length, items: sorted.slice(0, limit).map(trainingHit)};
}

// ---------------------------------------------------------------------------------------------
// Quick view
// ---------------------------------------------------------------------------------------------

const PARTICIPANT_ENTITY: Record<EventParticipantType, CrudEntityTypes> = {
    Employee: CrudEntityTypes.EMPLOYEE,
    Student: CrudEntityTypes.STUDENT,
    Parent: CrudEntityTypes.PARENT,
};

function readableNameTypes(): EventParticipantType[] {
    return (Object.keys(PARTICIPANT_ENTITY) as EventParticipantType[])
        .filter((type) => canReadType(PARTICIPANT_ENTITY[type], 'firstName', 'lastName'));
}

/** Populate of the single training, each reference only if the user may read the names */
function detailPopulate(id: Types.ObjectId): Record<string, { fields: string[] }> {
    const populate = topicPopulate();
    if (canReadEntity(CrudEntityTypes.EVENT_INFO, id, 'organizerList') && canReadType(CrudEntityTypes.EMPLOYEE, 'firstName', 'lastName')) {
        populate.organizerList = {fields: ['firstName', 'lastName']};
    }
    if (canReadEntity(CrudEntityTypes.EVENT_INFO, id, 'participantList') && readableNameTypes().length) {
        populate.participantList = {fields: ['firstName', 'lastName']};
    }
    return populate;
}

/** First name of a populated participant whose type the user may read; '' otherwise */
function firstNameOf(item: EventParticipantListItem, readable: EventParticipantType[]): string {
    if (!readable.includes(item.type) || !item.participant || isObjectId(item.participant)) {
        return '';
    }
    const person = item.participant as { firstName?: string; lastName?: string };
    return (person.firstName || person.lastName || '').trim();
}

function shortText(text: string, max: number): string {
    const value = text.replace(/\s+/g, ' ').trim();
    return value.length > max ? `${value.slice(0, max - 1)}…` : value;
}

/** Address lines of a face-to-face training (room / building / street / city) */
function addressText(training: EventInfo): string {
    const venue = training.venue;
    if (!venue || !venue.isFaceToFaceEvent) {
        return '';
    }
    return [
        [venue.street, venue.houseNumber].filter(Boolean).join(' '),
        [venue.postal, venue.city].filter(Boolean).join(' '),
    ].filter((el) => el).join(', ');
}

function capacityText(training: EventInfo): string {
    const capacity = capacityOf(training);
    const occupied = occupiedSpots(training);
    if (capacity === null) {
        return `${occupied} belegt · ohne Platzbegrenzung`;
    }
    const free = freeSpots(training);
    return `${occupied} von ${capacity} belegt · ${free ? `${free} frei` : 'ausgebucht'}`;
}

/** Participants grouped by status: "bestätigt (3)": "Anna, Ben, Clara" */
function participantFacts(training: EventInfo, ctx: QuickContext) {
    const participants = participantsOf(training);
    if (!participants.length) {
        ctx.facts.push({label: 'Teilnehmende', value: 'noch niemand eingetragen'});
        return;
    }
    const readable = readableNameTypes();
    for (const item of STATUS_ITEMS) {
        const group = participants.filter((participant) => statusKey(participant) === item.value);
        if (!group.length) {
            continue;
        }
        const names = group.map((participant) => firstNameOf(participant, readable)).filter((el) => el);
        const shown = names.slice(0, MAX_NAMES);
        const rest = group.length - shown.length;
        const value = shown.length ? `${shown.join(', ')}${rest > 0 ? ` +${rest}` : ''}` : String(group.length);
        ctx.facts.push({label: `${item.text} (${group.length})`, value});
    }
}

function venueFacts(training: EventInfo, withSummary: boolean, ctx: QuickContext) {
    if (withSummary) {
        ctx.facts.push({label: 'Ort', value: venueSummary(training).text});
    }
    const venue = training.venue;
    if (venue && venue.isOnlineEvent && venue.onlineLink) {
        ctx.facts.push({label: 'Link', value: venue.onlineLink});
    }
    const address = addressText(training);
    if (address) {
        ctx.facts.push({label: 'Adresse', value: address});
    }
}

/** Facts of a training for the quick view; date and venue only if the hit preview does not show them yet */
export async function loadTrainingFacts(id: Types.ObjectId, preview: Record<string, unknown>, ctx: QuickContext) {
    if (!canReadEntity(CrudEntityTypes.EVENT_INFO, id, 'startTime')) {
        return;
    }
    const training = await EventManagerApi.findEventInfoById(id, {populate: detailPopulate(id)} as any);
    if (!training) {
        return;
    }
    if (training.isCancelled) {
        ctx.tags.push({text: 'abgesagt', tone: 'tone-bad'});
    }
    const series = seriesLabel(training);
    if (series) {
        ctx.tags.push({text: `Serie · ${series}`, tone: 'tone-purple'});
    }
    if (!preview.when && training.startTime) {
        ctx.facts.push({label: 'Datum', value: longDate(training.startTime)});
        ctx.facts.push({label: 'Zeit', value: [timeRange(training), formatDuration(training.durationInMinutes)].filter((el) => el).join(' · ')});
    } else if (training.durationInMinutes) {
        ctx.facts.push({label: 'Dauer', value: formatDuration(training.durationInMinutes)});
    }
    ctx.facts.push({label: 'Thema', value: topicName(training)});
    if (training.title) {
        ctx.facts.push({label: 'Titel', value: training.title});
    }
    if (training.description) {
        ctx.facts.push({label: 'Beschreibung', value: shortText(training.description, MAX_DESCRIPTION)});
    }
    if (training.venue) {
        venueFacts(training, !preview.venue, ctx);
    }
    const organizers = organizerNames(training);
    if (organizers.length) {
        ctx.facts.push({label: 'Leitung', value: organizers.join(', ')});
    }
    if (Array.isArray(training.participantList)) {
        ctx.facts.push({label: 'Plätze', value: capacityText(training)});
        participantFacts(training, ctx);
    }
}
